<script setup lang="ts">
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import type { LogicalEdge, OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import type { Post } from '@@/types'
import SurfacePostCompressed from '@@/vuecomponents/SurfacePostCompressed.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import SurfacePostDragPreview from '@@/vuecomponents/SurfacePostDragPreview.vue'
import { useSurfacePostDraggableHelper } from '@@/vuecomposables/surface_post_draggable_helper'
import { usePreferredDark } from '@vueuse/core'
import { computed, ref } from 'vue'

const itemEl = ref<HTMLElement | undefined>(undefined)
const props = withDefaults(
  defineProps<{
    post: Post
    dragElementType: string
    dropElementType: string
    axis: 'vertical' | 'horizontal'
    gap: number
    shouldCompress: boolean
    isDraggable: boolean

    // For SurfacePost.vue
    index: number
    postReactionIds: number[]
    postCommentIds: number[]
    putAttachmentOnTop?: boolean
    xShadow?: boolean
    lazyRender?: boolean
    minPostHeight?: number
    minPostWidth?: number
  }>(),
  {
    putAttachmentOnTop: false,
    xShadow: true,
    lazyRender: false,
    minPostHeight: 0,
    minPostWidth: 0,
  },
)

const emit = defineEmits<{
  (e: 'on-drag-start'): void
  (e: 'on-drop', payload: OnDropPayload<{ post: Post }, { post: Post }>): void
}>()

const itemData = computed<{ post: Post }>(() => ({ post: props.post }))
const elementRef = computed<HTMLElement | undefined>(() => {
  if (itemEl.value === undefined) return undefined
  return itemEl.value
})

const { isDragging } = useDraggableElement({
  elementRef,
  type: props.dragElementType,
  data: itemData,
  dragPreview: SurfacePostDragPreview,
  dragPreviewComponentProps: itemData as any,
  isDraggable: computed(() => props.isDraggable),
  onDragStart: () => {
    emit('on-drag-start')
  },
})

const fixedEdge = ref<LogicalEdge | undefined>(undefined)
const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef,
  type: props.dropElementType,
  data: itemData,
})

const { canDropPost, getEdgeForDropTargetPost } = useSurfacePostDraggableHelper()
addDraggableSource<{ post: Post }>({
  type: props.dragElementType,
  axis: props.axis,
  onDrag: (e) => {
    const dropTargetPost = (e.self.data.data as any).post
    const sourcePost = (e.source.data.data as any).post
    fixedEdge.value = getEdgeForDropTargetPost(dropTargetPost, sourcePost)
  },
  onDragLeave: () => {
    fixedEdge.value = undefined
  },
  canDrop: (e) => {
    const draggedPost = e.sourceItem.data.post
    const targetPost = e.targetItem.data.post
    return canDropPost(draggedPost, targetPost)
  },
  onDrop: (e) => {
    emit('on-drop', e)
    fixedEdge.value = undefined
  },
})

const isDarkMode = usePreferredDark()
const actualEdge = computed(() => fixedEdge.value ?? closestEdgeToPointer.value)
</script>

<template>
  <div ref="itemEl" class="flex flex-col relative">
    <SurfacePost
      v-show="!shouldCompress"
      :class="[
        // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/grid.scss
        'wish-v2',
        isDragging && 'opacity-40',
      ]"
      :index="index"
      :post="post"
      :post-reaction-ids="postReactionIds"
      :post-comment-ids="postCommentIds"
      :put-attachment-on-top="putAttachmentOnTop"
      :x-shadow="xShadow"
      :lazy-render="lazyRender"
      :min-post-height="minPostHeight"
      :min-post-width="minPostWidth"
    />
    <SurfacePostCompressed v-show="shouldCompress" :post="post" :class="[isDragging && 'opacity-40']" />
    <OzDropIndicatorLine v-if="actualEdge" :edge="actualEdge" :gap="gap" :dark-mode="isDarkMode" class="mx-1" />
  </div>
</template>
